//
//  SDKToolsTokenTransform.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { TokenType } from "../../model/enums/SDKTokenType";
import { Unit } from "../../model/enums/SDKUnit";
import { ColorToken } from "../../model/tokens/SDKColorToken";
import { ShadowToken } from "../../model/tokens/SDKShadowToken";
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import tinycolor from "tinycolor2";
function roundToSingeDecimal(value) {
    return Math.round(value * 10) / 10;
}
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Transformer instance
/** Token data transformer. Use for direct representation of token to other representations, such as CSS, rgb etc. More transformers will be added over time */
export class TokenTransform {
    // --- Conversion to CSS
    /** Converts a single token to CSS definition. Values are always resolved to plain values, references to other tokens are not yet supported */
    tokenToCSS(token) {
        switch (token.tokenType) {
            case TokenType.color:
                return this.colorTokenToCSS(token.value);
            case TokenType.border:
                return this.borderTokenToCSS(token.value);
            case TokenType.gradient:
                return this.gradientsTokenToCSS(token.value);
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                return this.dimensionTokenToCSS(token.value);
            case TokenType.shadow:
                return this.shadowsTokenToCSS(token.value);
            case TokenType.fontWeight:
            case TokenType.fontFamily:
            case TokenType.productCopy:
            case TokenType.string:
                return this.stringTokenToCSS(token.value);
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                return this.optionTokenToCSS(token.value);
            case TokenType.blur:
                return this.blurTokenToCSS(token.value);
            case TokenType.typography:
                return this.typographyTokenToCSS(token.value);
            default:
                throw new UnreachableCaseError(token.tokenType, "Unsupported token type for transformation to CSS:");
        }
    }
    /** Converts token to appropriate css variable definition --(name): css_definition */
    tokenToCSSVariableDeclaration(token, variableName) {
        return `--${variableName}: ${this.tokenToCSS(token)}`;
    }
    /** Converts color token value to css definition */
    colorTokenToCSS(value, dedicatedOpacity) {
        const color = tinycolor(ColorToken.colorValueToHex8(value));
        return color.toRgbString();
    }
    /** Converts border token value to css definition */
    borderTokenToCSS(value) {
        return `${this.dimensionTokenToCSS(value.width)} solid ${this.colorTokenToCSS(value.color)}, ${value.position.toLowerCase()}`;
    }
    /** Converts shadow token and opacity by merging two opacities and using color token opacity as fallback */
    shadowTokenColorAndOpacityToCSS(value) {
        const color = tinycolor(ShadowToken.shadowTokenColorAndOpacityToHex8(value));
        return color.toRgbString();
    }
    /** Converts shadow token value to css definition */
    shadowsTokenToCSS(value) {
        return value
            .map((shadowValue) => this.shadowTokenToCSS(shadowValue))
            .join(", ");
    }
    shadowTokenToCSS(value) {
        return `${this.shadowTokenColorAndOpacityToCSS(value)} ${value.type === "Inner" ? "inset " : ""}${value.x}px ${value.y}px ${value.radius}px ${value.spread}px`;
    }
    /** Converts typography token value to css definition */
    typographyTokenToCSS(value) {
        const fontName = `${value.fontFamily.text} ${value.fontWeight.text}`;
        const fontValue = this.dimensionTokenToCSS(value.fontSize);
        let textDecoration = "";
        let textCase = "";
        if (value.textDecoration.value !== "None") {
            textDecoration = `, ${value.textDecoration.value.toLowerCase()}`;
        }
        if (value.textCase.value !== "Original") {
            textCase = `, ${value.textCase.value.toLowerCase()}`;
        }
        return `${fontName} ${fontValue}${textDecoration}${textCase}`;
    }
    /** Converts border token value to css definition */
    blurTokenToCSS(value) {
        return `blur(${this.dimensionTokenToCSS(value.radius)})`;
    }
    /** Converts border token value to css definition */
    dimensionTokenToCSS(value) {
        return `${value.measure}${this.unitToCSS(value.unit)}`;
    }
    unitToCSS(value) {
        switch (value) {
            case Unit.rem:
                return "rem";
            case Unit.percent:
                return "%";
            case Unit.pixels:
                return "px";
            case Unit.raw:
                return "";
            case Unit.ms:
                return "ms";
            default:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw SupernovaError.fromMessage(`Unsupported unit: ${value}`);
        }
    }
    gradientsTokenToCSS(value) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/unbound-method
        return value.map(this.gradientTokenToCSS).join(" "); // FIXME: Layers
    }
    gradientTokenToCssFunction(value) {
        switch (value.type) {
            case "Linear":
                return "linear-gradient";
            case "Radial":
                return "radial-gradient";
            case "Angular":
                return "conic-gradient";
            default:
                throw new Error("Unsupported gradient type");
        }
    }
    gradientTokenToCssDirection(value) {
        const deltaX = roundToSingeDecimal(value.to.x) - roundToSingeDecimal(value.from.x);
        const deltaY = roundToSingeDecimal(value.to.y) - roundToSingeDecimal(value.from.y);
        const rad = Math.atan2(deltaY, deltaX);
        const deg = rad * (180 / Math.PI);
        // This function is used to convert the angle to the correct value
        // The file used to verify this is: https://www.figma.com/file/2ekDhbY7BoDPe9Sr9oOQqN/Test---Gradients-(Copy)?type=design&node-id=0%3A1&mode=design&t=NFyvcNPiuy6xyYrS-1
        // Specifically, the comments in the following IFs correspond to the right angled examples in the file
        // The file also contains angles that are not right angled - they also work correctly with this function
        const getAngle = () => {
            if (deltaX >= 0 && deltaY > 0) {
                // top to bottom is 90deg but should be 180deg
                return 90 + deg;
            }
            if (deltaX > 0 && deltaY <= 0) {
                // left to right is 0deg but should be 90deg
                return 90 + deg;
            }
            if (deltaX <= 0 && deltaY < 0) {
                // bottom to top is -90deg but should be 0deg
                return 90 + deg;
            }
            // right to left is 180deg but should be -90deg
            return deg - 270;
        };
        switch (value.type) {
            case "Linear":
                return `${getAngle()}deg`;
            case "Radial":
                return "circle";
            case "Angular":
                return "";
            default:
                throw new Error("Unsupported gradient type");
        }
    }
    /** Converts gradient token value to css definition */
    gradientTokenToCSS(value) {
        const gradientDirectionString = this.gradientTokenToCssDirection(value);
        let gradientTypeString = `${this.gradientTokenToCssFunction(value)}(`;
        if (gradientDirectionString) {
            gradientTypeString += `${gradientDirectionString}, `;
        }
        // Describe gradient as (type) (stop1, stop2 ...)
        // Example: radial-gradient(circle, rgba(63,94,251,1) 0%, rgba(252,70,107,1) 100%);
        const stops = value.stops
            .map((stop) => {
            return `${this.colorTokenToCSS(stop.color)} ${stop.position * 100}%`;
        })
            .join(", ");
        return `${gradientTypeString}${stops})`;
    }
    /** Converts generic token value to css definition */
    stringTokenToCSS(value) {
        return `"${value.text}"`;
    }
    // TODO: Separate logic for different types?
    optionTokenToCSS(value) {
        return `"${value.value}"`;
    }
    // --- Special treatment for CSS
    /** Converts a single color token to #hheexx or #hheexxaa */
    colorTokenToHEXorHEXA(colorToken) {
        const color = tinycolor(colorToken.toHex8());
        return colorToken.value.opacity.measure === 1
            ? color.toHexString()
            : color.toHex8String();
    }
    /** Converts a single color token to rgba(x,x,x,x)  */
    colorTokenToRGBA(colorToken) {
        const color = tinycolor(colorToken.toHex8());
        return color.toRgbString();
    }
    /** Converts a single color token to rgba(x,x,x,x) with relative values */
    colorTokenToPercenageRGBA(colorToken) {
        const color = tinycolor(colorToken.toHex8());
        return color.toPercentageRgbString();
    }
    /** Converts a single color token to hsla(x,x,x,x) */
    colorTokenToHSLA(colorToken) {
        const color = tinycolor(colorToken.toHex8());
        return color.toHslString();
    }
    /** Converts a single color token to hsva(x,x,x,x) */
    colorTokenToHSVA(colorToken) {
        const color = tinycolor(colorToken.toHex8());
        return color.toHsvString();
    }
}
